import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Users, Dumbbell, Calendar, Zap, Target, Activity } from "lucide-react";
import { getSiteSetting, listNews, listMatches } from "../lib/db";
import { useSiteImage } from "../context/SiteImagesContext";
import CalendarioWidget from "../components/CalendarioWidget";

export default function Home() {
  const [news, setNews] = useState([]);
  const [matches, setMatches] = useState([]);
  const [stagione, setStagione] = useState("2026/27");
  const heroImg = useSiteImage("home_hero", "https://images.pexels.com/photos/11097680/pexels-photo-11097680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600");
  const teamCardImg = useSiteImage("home_card_team", "https://images.unsplash.com/photo-1522778526097-ce0a22ceb253?w=1200&q=80");

  useEffect(() => {
    listNews().then((d) => setNews(d.slice(0, 3))).catch(() => {});
    listMatches().then(setMatches).catch(() => {});
    getSiteSetting("stagione_corrente").then((d) => {
      if (d) setStagione(d.replace("/", "/"));
    }).catch(() => {});
  }, []);

  // Mostra sempre la prima partita programmata che non è ancora passata.
  // Il calendario ordina già le partite per data, ma qui filtriamo anche la data
  // per evitare che una gara rimasta "scheduled" nel database blocchi la Home.
  const today = new Date().toISOString().split("T")[0];
  const upcoming = matches
    .filter((m) => m.status === "scheduled" && m.date >= today)
    .slice(0, 1)[0];
  const lastResult = [...matches].reverse().find((m) => m.status === "played");

  return (
    <div data-testid="home-page" style={{backgroundColor: '#002749'}}>
      <Helmet>
  <title>ASD Rubiana Calcio – Squadra & Palestra in Val di Susa</title>
  <meta name="description" content="ASD Rubiana Calcio: squadra di calcio e palestra nel cuore della Val di Susa. Scopri il calendario, la rosa, i corsi e le ultime news." />
  <meta property="og:title" content="ASD Rubiana Calcio – Squadra & Palestra" />
  <meta property="og:description" content="Squadra di calcio e palestra nel cuore della Val di Susa." />
  <meta property="og:url" content="https://rubianacalcio.it" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://rubianacalcio.it/logo512.png" />
</Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt=""
            className="h-full w-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#002749] via-[#002749]/80 to-[#002749]/30" />
          <div className="absolute inset-0 grid-pattern opacity-50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 lg:pt-32 lg:pb-44">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 mb-6 animate-fade-up">
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 animate-pulse" />
                <span className="text-overline text-yellow-400">Stagione {stagione} attiva</span>
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter uppercase leading-[0.9] text-zinc-50 animate-fade-up">
                <span className="text-yellow-400">Passione</span><br />
                <span className="relative inline-block">
                  Rubiana
                  <span className="absolute -bottom-2 left-0 right-0 h-1.5 bg-blue-600" />
                </span>
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed animate-fade-up" style={{ animationDelay: "0.15s", opacity: 0 }}>
                ASD Rubiana Calcio è la casa sportiva di chi vive la valle: una squadra di calcio
                che lotta su ogni pallone e una palestra dove la community si allena ogni giorno.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.3s", opacity: 0 }}>
                <Link to="/palestra" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-yellow-400 text-zinc-950 font-display font-bold hover:bg-yellow-300 transition-colors active:scale-95" data-testid="hero-cta-courses">
                  Scopri i corsi <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/squadra" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-zinc-700 hover:border-zinc-400 text-zinc-100 font-display font-bold transition-colors" data-testid="hero-cta-team">
                  La prima squadra
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4">
              {upcoming && (
                <Link to="/calendario" className="block glass-card rounded-2xl p-5 hover:border-yellow-400/40 transition-colors cursor-pointer" data-testid="hero-upcoming-match">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-overline text-yellow-400">Prossima gara</span>
                    <Calendar className="h-4 w-4 text-zinc-500" />
                  </div>
                  <div className="text-xs text-zinc-500 mb-3">{new Date(upcoming.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
                  {upcoming.title ? (
                    <div className="font-display font-bold text-lg leading-tight text-center">{upcoming.title}</div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 text-right">
                        <div className="font-display font-bold text-lg leading-tight">{upcoming.home}</div>
                      </div>
                      <div className="font-display text-2xl font-black text-yellow-400">VS</div>
                      <div className="flex-1 text-left">
                        <div className="font-display font-bold text-lg leading-tight">{upcoming.away}</div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 text-xs text-zinc-500">{upcoming.competition}{upcoming.competition && upcoming.venue ? " • " : ""}{upcoming.venue}</div>
                </Link>
              )}
              
              {lastResult && (
                <Link to="/calendario" className="block glass-card rounded-2xl p-5 hover:border-yellow-400/40 transition-colors cursor-pointer" data-testid="hero-last-result">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-overline text-yellow-400">Ultimo risultato</span>
                    <Trophy className="h-4 w-4 text-zinc-500" />
                  </div>
                  <div className="text-xs text-zinc-500 mb-3">Partita disputata</div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 text-right">
                      <div className="font-display font-bold text-lg leading-tight">{lastResult.home}</div>
                    </div>
                    <div className="font-display text-2xl font-black text-yellow-400">
                      {lastResult.home_score} - {lastResult.away_score}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-display font-bold text-lg leading-tight">{lastResult.away}</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-zinc-500">{lastResult.competition} • Risultato finale</div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS / Bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
          <div className="max-w-2xl">
            <div className="text-overline text-yellow-400 mb-3">// Una società, due passioni</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
              Calcio e <span className="text-yellow-400">palestra</span>,<br />sotto lo stesso tetto.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm">
            Da oltre 20 anni promuoviamo lo sport in valle. La nostra missione è semplice:
            tenere viva la passione del territorio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 rounded-3xl bg-zinc-900 border border-zinc-800 overflow-hidden group hover:border-yellow-400/40 transition-colors" data-testid="card-team">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={teamCardImg} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 text-yellow-400 mb-3"><Trophy className="h-4 w-4" /><span className="text-overline">Prima squadra</span></div>
              <h3 className="font-display font-bold text-2xl sm:text-3xl mb-3">In campo nel girone A di Terza Categoria di Pinerolo</h3>
              <p className="text-sm text-zinc-400 mb-5 leading-relaxed">Una rosa giovane allenata da Mister De Brito che gioca un calcio offensivo e di carattere. Calendario, classifica e roster sono sempre aggiornati.</p>
              <Link to="/squadra" className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-yellow-400 hover:gap-3 transition-all">
                Scopri la rosa <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div 
  className="md:col-span-5 rounded-3xl p-8 flex flex-col justify-between min-h-[360px] relative overflow-hidden" 
  data-testid="card-gym"
  style={{
    backgroundImage: 'url(https://okatgbxionqjmzhnazqx.supabase.co/storage/v1/object/public/Immagini%20Sito/4be16e3efd964922ae72609580c5d8f4.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }}
>
  <div className="absolute inset-0 bg-blue-900/60 rounded-3xl" />
  <div className="relative z-10">
    <Dumbbell className="h-8 w-8 mb-6" />
    <h3 className="font-display font-bold text-2xl sm:text-3xl mb-3 leading-tight">Corsi in palestra, una community</h3>
    <p className="text-sm text-blue-100 leading-relaxed">Functional, Pilates, Ginnastica Dolce, Judo Bimbi. Trovi l'allenamento giusto per te.</p>
  </div>
  <div className="relative z-10">
    <Link to="/palestra" className="inline-flex w-fit items-center gap-2 px-5 py-2.5 mt-6 rounded-full bg-zinc-950 text-zinc-50 hover:bg-zinc-800 font-display font-bold text-sm transition-colors">
      Vedi orari <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
</div>

          <div className="md:col-span-4 rounded-3xl bg-zinc-900 border border-zinc-800 p-8" data-testid="card-stat-1">
            <Users className="h-7 w-7 text-yellow-400 mb-4" />
            <div className="font-display font-black text-5xl text-zinc-50">130+</div>
            <div className="text-sm text-zinc-400 mt-1">Tesserati attivi</div>
          </div>
          <div className="md:col-span-4 rounded-3xl bg-zinc-900 border border-zinc-800 p-8" data-testid="card-stat-2">
            <Dumbbell className="h-7 w-7 text-yellow-400 mb-4" />
            <div className="font-display font-black text-5xl text-zinc-50">4</div>
            <div className="text-sm text-zinc-400 mt-1">Corsi disponibili</div>
          </div>
          <div className="md:col-span-4 rounded-3xl bg-zinc-900 border border-zinc-800 p-8" data-testid="card-stat-3">
            <Activity className="h-7 w-7 text-yellow-400 mb-4" />
            <div className="font-display font-black text-5xl text-zinc-50">7</div>
            <div className="text-sm text-zinc-400 mt-1">Giorni di allenamento</div>
          </div>
        </div>
      </section>

      {/* Calendario preview */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
  <div className="flex items-end justify-between mb-10">
    <div>
      <div className="text-overline text-yellow-400 mb-3">// Agenda</div>
      <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
        Prossimi <span className="text-yellow-400">eventi</span>
      </h2>
    </div>
  </div>
  <CalendarioWidget tipo="entrambi" compact={true} />
</section>

      {/* News strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-overline text-yellow-400 mb-3">// Aggiornamenti</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">Ultime news</h2>
          </div>
          <Link to="/news" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-display font-bold text-zinc-400 hover:text-yellow-400">
            Tutte le news <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {news.map((n) => (
  <Link to={`/news/${n.id}`} key={n.id} className="rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-colors group block" data-testid={`home-news-${n.id}`}>
    <div className="aspect-[16/10] overflow-hidden">
      <img src={n.image} alt={n.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
    </div>
    <div className="p-6">
      <div className="text-overline text-yellow-400 mb-2">{n.category}</div>
      <h3 className="font-display font-bold text-lg leading-snug mb-2 group-hover:text-yellow-400 transition-colors">{n.title}</h3>
      <p className="text-sm text-zinc-400 line-clamp-2">{n.excerpt}</p>
    </div>
  </Link>
))}
        </div>
      </section>
    </div>
  );
}